import { useQuery } from '@apollo/client';
import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import { NotFound } from '~/components/common/NotFound';
import { PageHeading } from '~/components/common/PageHeading';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { useBreadcrumb } from '~/components/layout/Breadcrumb';
import { Button } from '~/components/ui/button';
import { CollapsibleCard } from '~/components/ui/collapse';
import { DeleteOtherWiki } from '~/components/upload/other-wiki/delete-other-wiki';
import { UpdateOtherWikiForm } from '~/components/upload/other-wiki/update-other-wiki-form';
import { useRouteParam } from '~/hooks/routing';
import { otherWikiDetailRoute, uploadOtherWikiRoute } from '~/paths';

const UPLOAD_OTHER_WIKI_UPDATE_PAGE = graphql(`
  query UploadOtherWikiUpdatePage($id: ID!) {
    otherWikiGet(id: $id) {
      ...otherWikiParts
    }
  }
`);

export default function UploadOtherWikiUpdatePage() {
  return (
    <ApolloProviderV4>
      <UploadOtherWikiUpdatePageInner />
    </ApolloProviderV4>
  );
}

function UploadOtherWikiUpdatePageInner() {
  const navigate = useNavigate();
  const otherWikiId = useRouteParam('otherWikiId', parseInt);

  const { data, loading } = useQuery(UPLOAD_OTHER_WIKI_UPDATE_PAGE, {
    variables: { id: otherWikiId },
  });

  const otherWiki = data?.otherWikiGet;

  useBreadcrumb(
    'routes/upload/other/$otherWikiId',
    otherWiki?.title ?? 'Update Other Wiki',
  );

  if (loading) return <SpinnerPlaceholder />;
  if (!otherWiki) return <NotFound />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-end gap-2">
        <PageHeading>{otherWiki.title}</PageHeading>
        <Link
          className="btn btn-primary btn-sm"
          to={otherWikiDetailRoute(parseInt(otherWiki.id))}
        >
          View page <FontAwesomeIcon icon={faExternalLink} />
        </Link>
      </div>

      <UpdateOtherWikiForm otherWiki={otherWiki} />

      <CollapsibleCard title="Delete Other Wiki">
        <div className="text-center">
          <DeleteOtherWiki
            otherWikiId={parseInt(otherWiki.id)}
            onDeleteSuccess={() => navigate(uploadOtherWikiRoute())}
          >
            {handleDelete => (
              <Button
                type="button"
                color="error"
                onClick={handleDelete}
                loading={loading}
              >
                Delete Other Wiki
              </Button>
            )}
          </DeleteOtherWiki>
        </div>
      </CollapsibleCard>
    </div>
  );
}
